import { getPreset, type Placement, type Step } from '@eip/engine';
import { LOOK_LABELS } from './mockup-ui';
import type { Report, ReportCheck, ReportItem, ReportMockup, ReportSeparation } from './report';
import { summaryLabel } from './report';
import { MODE_LABELS } from './sep-ui';

/** Thumbnails for the mockup grid, keyed by `ReportMockup.file` — `data:` URLs
 *  from `reportThumbs`. Omitted (the ZIP's per-folder tickets, the JSON) and the
 *  mockup section lists its rows without images. */
export interface ReportHtmlOptions {
  thumbs?: Record<string, string>;
  /** Why there are no images: a failed `reportThumbs` still produces a ticket,
   *  and the shop has to know the pictures are missing rather than absent. */
  thumbsError?: string;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}

/** The file-status pill reuses the inspector's pass/warn/fail palette: 'ok' reads
 *  as pass (green), 'pending' as warn (amber, nothing to ship yet), 'failed' as fail. */
function statusPillClass(status: ReportItem['status']): 'pass' | 'warn' | 'fail' {
  return status === 'ok' ? 'pass' : status === 'failed' ? 'fail' : 'warn';
}

function formatParamValue(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map((x) => formatParamValue(x)).join(', ')}]`;
  return String(v);
}

function renderStepLine(step: Step): string {
  const state = step.enabled ? 'on' : 'off';
  const params = Object.entries(step.params as Record<string, unknown>)
    .map(([k, v]) => `${escapeHtml(k)}=${escapeHtml(formatParamValue(v))}`)
    .join(', ');
  return `<li><code>${escapeHtml(step.kind)}</code> (${state}): ${params}</li>`;
}

function renderCheckRow(c: ReportCheck): string {
  const value = c.unit ? `${c.value} ${escapeHtml(c.unit)}` : String(c.value);
  // `c.status` is a closed union from the worker, but this is a library
  // function: escape it in the class attribute too rather than trusting the
  // type at the one place a stray quote would break out of the attribute.
  return `<tr><td><span class="pill ${escapeHtml(c.status)}">${escapeHtml(c.status)}</span></td><td>${escapeHtml(c.label)}</td><td>${value}</td><td>${escapeHtml(c.message)}</td></tr>`;
}

/** "1024×979 px · png · srgb · 300 dpi", plus the assumed-density warning when it applies. */
function renderSource(item: ReportItem): string {
  const src = item.source;
  if (!src) return '';
  const line = `${src.width}×${src.height} px · ${escapeHtml(src.kind)} · ${escapeHtml(src.colorProfile)} · ${src.dpi} dpi`;
  const assumed = src.dpiAssumed
    ? `<p class="assumed">Size assumed at ${src.dpi} dpi (file carried no density)</p>`
    : '';
  return `<p class="source">Source: ${line}</p>${assumed}`;
}

const pct = (n: number) => `${(n * 100).toFixed(1)} %`;

/** A colour is interpolated into a `style` attribute, so it is validated rather
 *  than merely escaped — same rule as the header's garment swatch. */
function swatch(hex: string): string {
  return `<span class="swatch" style="background:${/^#[0-9a-f]{6}$/i.test(hex) ? hex : '#000000'}"></span>`;
}

function renderInkRow(ink: ReportSeparation['inks'][number]): string {
  // `order` is 0-based in the model; the press (and the film file names) count from 1.
  return `<tr><td>${ink.order + 1}</td><td>${swatch(ink.hex)}${escapeHtml(ink.name)}</td><td>${escapeHtml(ink.hex)}</td><td>${pct(ink.coverage)}</td></tr>`;
}

/**
 * The screen-print half of a file's section: what to mix, in what order, at
 * what screening, on what film. Mesh/LPI/angle are one line above the table
 * rather than a column repeated on every row — they are per-separation, not
 * per-ink.
 */
function renderSeparation(sep: ReportSeparation | undefined): string {
  if (!sep) return '';
  const mode = MODE_LABELS[sep.mode]?.label ?? sep.mode;
  const screening = [
    escapeHtml(mode), `${sep.screen.mesh} mesh`, `${sep.screen.lpi} lpi`,
    `${sep.screen.angle}° ${escapeHtml(sep.screen.angleMode)}`, escapeHtml(sep.screen.shape), `${sep.screen.filmDpi} dpi film`,
  ].join(' · ');
  // The table is emitted even with no rows: the section's shape (and anything
  // that reads it) stays the same whether or not an ink is switched on.
  const inks = `<table class="inks"><thead><tr><th>#</th><th>Ink</th><th>Hex</th><th>Coverage</th></tr></thead><tbody>${sep.inks.map(renderInkRow).join('')}</tbody></table>`
    + (sep.inks.length ? '' : '<p class="muted">No inks enabled.</p>');
  const plates = sep.plates.length
    ? `<p class="plates">Plates: ${sep.plates.map((p) => `${escapeHtml(p.name)} ${pct(p.coverage)}`).join(' · ')}</p>`
    : '';
  const unmatched = sep.unmatched > 0 ? `<p class="assumed">Unmatched colours: ${pct(sep.unmatched)} of opaque pixels match no ink.</p>` : '';
  // A failed rebuild leaves the PREVIOUS build's plates on screen and in this
  // ticket; the operator has to know the numbers above may not be current.
  const buildError = sep.error ? `<p class="run-error">Separation build failed: ${escapeHtml(sep.error)} — these plates are from the previous build.</p>` : '';
  const job = sep.settings.film.jobName ? `Job: ${escapeHtml(sep.settings.film.jobName)} · ` : '';
  const film = `${job}Film: ${escapeHtml(sep.film.size)} · ${sep.film.mirror ? 'mirrored' : 'not mirrored'} · ${sep.film.marks ? 'marks' : 'no marks'}`;
  return `<h3>Inks</h3>
    <p class="screening">${screening}</p>
    ${inks}
    ${plates}
    ${unmatched}
    ${buildError}
    <p class="film">${film}</p>`;
}

/**
 * "10.0 in wide · centred across · 1.5 in from top" — the print as it sits on the blank.
 *
 * Either axis can be CENTRED (`Placement`'s `'center'`, the panel's two toggles), and each says so
 * in its own words: "centred across · centred down" is a print in the middle of the area, which is
 * not the same instruction to the press as a pair of inch offsets that happen to come out there
 * today. The axis is named because the two read side by side — a bare "centred" twice over would
 * leave the press counting separators to work out which was which.
 */
function placementLine(p: Placement): string {
  const across = p.xIn === 'center' ? 'centred across' : `${p.xIn.toFixed(1)} in from left`;
  const down = p.yIn === 'center' ? 'centred down' : `${p.yIn.toFixed(1)} in from top`;
  return `${p.widthIn.toFixed(1)} in wide · ${across} · ${down}`;
}

/** `rotated -90°`, or nothing for an unturned print. */
const rotationNote = (deg: number) => (deg ? ` rotated ${deg}°` : '');

function renderMockupRow(m: ReportMockup): string {
  const look = LOOK_LABELS[m.look] ?? m.look;
  // Everything the job actually prints, in the order it composites: the primary placement, its
  // rotation, then one note per extra print naming its area and the design that goes in it. An
  // area the render SKIPPED (its design is closed, or this blank has no such area) says so
  // instead: it was never printed, and a row describing it as a print describes a garment nobody
  // can produce.
  // A skip says WHICH of the two it was: the area is on this blank and had no design to print, or
  // the blank has no such area at all and the print was retained from another one. The press acts
  // differently on each — find the file, or drop the print — so the ticket does not blur them.
  const extras = m.extraPrints
    .map((p) => (m.skipped.includes(p.areaId)
      ? ` · + ${p.areaName} (skipped: ${p.areaOnBlank ? 'no design' : 'no such area on this blank'})`
      : ` · + ${p.areaName}${p.docName ? ` (${p.docName})` : ''}${rotationNote(p.rotationDeg)}`))
    .join('');
  const print = placementLine(m.placement) + (m.rotationDeg ? ` ·${rotationNote(m.rotationDeg)}` : '') + extras + (m.clipped ? ' · clipped' : '');
  return `<tr><td>${escapeHtml(m.template)}</td><td>${escapeHtml(m.view)}</td><td>${swatch(m.hex)}${escapeHtml(m.colorName)}</td>`
    // The backdrop under the look, in the same cell: it is the other half of "what this garment
    // looks like" — the surface it is photographed on — and a column of its own would be `none` on
    // every row of every ticket a shop that never swaps one prints.
    + `<td>${escapeHtml(look)}<br><span class="muted">Backdrop: ${escapeHtml(m.backdrop)}</span></td>`
    + `<td>${escapeHtml(print)}</td><td><code>${escapeHtml(m.file)}</code></td></tr>`;
}

/** A thumbnail is inlined ONLY when it is a base64 `data:` URL of an image: this
 *  is a library function, and the value lands in an `src` attribute, so it is
 *  validated the same way the swatch colours are rather than trusted because the
 *  worker produced it. */
const THUMB_URL = /^data:image\/(?:jpeg|png);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * The mockup half of a file's section: the colourways this job would export, as
 * a thumbnail grid (when thumbnails were fetched) over a table naming the blank,
 * the colour, the ink look, the placement and the file each one writes.
 */
function renderMockups(mockups: ReportMockup[] | undefined, options: ReportHtmlOptions): string {
  if (!mockups || mockups.length === 0) return '';
  const thumbs = options.thumbs;
  const tiles = mockups
    .map((m) => ({ m, url: thumbs?.[m.file] }))
    .filter((t): t is { m: ReportMockup; url: string } => !!t.url && THUMB_URL.test(t.url));
  const grid = tiles.length
    ? `<div class="mockup-grid">${tiles.map(({ m, url }) =>
        `<figure><img src="${escapeHtml(url)}" alt="${escapeHtml(`${m.template} — ${m.colorName}`)}"><figcaption>${escapeHtml(m.colorName)}</figcaption></figure>`).join('')}</div>`
    : '';
  const note = options.thumbsError ? `<p class="assumed">Mockup thumbnails unavailable: ${escapeHtml(options.thumbsError)}</p>` : '';
  return `<section class="mockups">
    <h3>Mockups</h3>
    ${grid}
    ${note}
    <table><thead><tr><th>Blank</th><th>View</th><th>Colour</th><th>Look</th><th>Print</th><th>File</th></tr></thead><tbody>${mockups.map(renderMockupRow).join('')}</tbody></table>
  </section>`;
}

function renderFileSection(item: ReportItem, options: ReportHtmlOptions): string {
  const output = item.output
    ? `${item.output.width}×${item.output.height} px · ${item.output.widthIn.toFixed(2)} × ${item.output.heightIn.toFixed(2)} in at ${item.output.dpi} dpi`
    : '&mdash;';
  const checksTable = item.checks.length
    ? `<table><thead><tr><th>Status</th><th>Check</th><th>Value</th><th>Message</th></tr></thead><tbody>${item.checks.map(renderCheckRow).join('')}</tbody></table>`
    : '<p class="muted">No checks recorded.</p>';
  const warnings = item.warnings.length
    ? `<ul class="warnings">${item.warnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`
    : '';
  const runError = item.runError ? `<p class="run-error">Run failed: ${escapeHtml(item.runError)}</p>` : '';
  return `<section class="file">
    <h2>${escapeHtml(item.file)} <span class="pill ${statusPillClass(item.status)}">${escapeHtml(item.status)}</span></h2>
    ${renderSource(item)}
    <p class="output">Output: ${output}</p>
    <p class="summary">${escapeHtml(summaryLabel(item.summary))}</p>
    ${runError}
    ${checksTable}
    ${warnings}
    ${renderSeparation(item.separation)}
    ${renderMockups(item.mockups, options)}
  </section>`;
}

/**
 * A complete, self-contained HTML document — inline CSS only, no external
 * requests, no script — meant to be printed or saved as the print shop's job
 * ticket. Every value that originates from a document, the settings, or a
 * step's params is escaped before it lands in the markup.
 */
export function reportHtml(report: Report, options: ReportHtmlOptions = {}): string {
  const { settings, steps, items, generatedAt, app } = report;
  const widthLabel = settings.targetWidthIn ? `${settings.targetWidthIn} in` : 'native';
  let presetName = '';
  try { presetName = getPreset(settings.presetId).name; } catch { presetName = ''; }
  // The swatch interpolates this into a `style` attribute, so it is validated
  // rather than merely escaped: `reportHtml` is a library function and must be
  // safe on its own, not only behind the colour input and `sanitizeSettings`.
  const garmentHex = escapeHtml(settings.garmentHex);
  const swatchHex = /^#[0-9a-f]{6}$/i.test(settings.garmentHex) ? settings.garmentHex : '#000000';
  const generatedLabel = escapeHtml(new Date(generatedAt).toLocaleString());

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(app)} report</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { font: 12pt/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif; margin: 24px; color: #111; background: #fff; }
  h1 { font-size: 18pt; margin: 0 0 4px; }
  h2 { font-size: 13pt; margin: 0 0 8px; display: flex; align-items: center; gap: 8px; }
  h3 { font-size: 11pt; margin: 14px 0 4px; }
  header.report-header { border-bottom: 2px solid #222; padding-bottom: 12px; margin-bottom: 20px; }
  .meta-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 10pt; color: #333; margin-top: 6px; }
  .swatch { display: inline-block; width: 12px; height: 12px; border-radius: 2px; border: 1px solid #999; vertical-align: -1px; margin-right: 4px; }
  section.file { border: 1px solid #ccc; border-radius: 6px; padding: 14px 16px; margin-bottom: 18px; }
  p.output, p.source { font-size: 10pt; color: #333; margin: 0 0 6px; }
  p.assumed { font-size: 10pt; color: #7a4b00; margin: 0 0 6px; }
  p.summary { font-weight: 600; margin: 0 0 10px; }
  p.run-error { color: #8a1c1c; font-weight: 600; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; margin-top: 6px; }
  th, td { text-align: left; border-bottom: 1px solid #ddd; padding: 4px 8px; }
  ul.warnings { font-size: 10pt; color: #7a4b00; margin: 10px 0 0; padding-left: 18px; }
  p.screening, p.plates, p.film { font-size: 10pt; color: #333; margin: 6px 0 0; }
  table.inks td:first-child { width: 2em; color: #666; }
  .muted { color: #777; }
  .pill { display: inline-block; min-width: 46px; text-align: center; padding: 1px 8px; border-radius: 999px; font-size: 9pt; font-weight: 600; }
  .pill.pass { background: #d6f2df; color: #0e6b2c; }
  .pill.warn { background: #fbe6c2; color: #8a5a00; }
  .pill.fail { background: #f6d3d3; color: #8a1c1c; }
  .pill.info { background: #d6e4fb; color: #1c3f8a; }
  section.mockups { margin-top: 12px; }
  .mockup-grid { display: flex; flex-wrap: wrap; gap: 12px; margin: 8px 0 4px; }
  .mockup-grid figure { margin: 0; width: 160px; }
  .mockup-grid img { width: 100%; height: auto; border: 1px solid #ccc; border-radius: 4px; display: block; }
  .mockup-grid figcaption { font-size: 9pt; color: #333; text-align: center; margin-top: 3px; }
  section.pipeline ul { font-size: 10pt; padding-left: 18px; }
  section.pipeline code { background: #f0f0f0; padding: 1px 4px; border-radius: 3px; }
  @media print {
    body { margin: 0.4in; }
    .file { break-inside: avoid; }
    @page { size: auto; margin: 0.5in; }
  }
</style>
</head>
<body>
<header class="report-header">
  <h1>${escapeHtml(app)} — Job Ticket</h1>
  <div class="meta-row">
    <span>Generated: ${generatedLabel}</span>
    <span><span class="swatch" style="background:${swatchHex}"></span>Garment: ${garmentHex}</span>
    <span>Print width: ${escapeHtml(widthLabel)}</span>
    <span>DPI: ${settings.dpi}</span>
    <span>Preset: ${escapeHtml(settings.presetId)}${presetName ? ` (${escapeHtml(presetName)})` : ''}</span>
  </div>
</header>
${items.map((item) => renderFileSection(item, options)).join('\n')}
<section class="pipeline">
  <h2>Pipeline</h2>
  <ul>${steps.map(renderStepLine).join('')}</ul>
</section>
</body>
</html>`;
}
